import { useEffect, useRef } from 'react'

import { ACCESS_TOKEN_KEY } from 'src/app-constants'

/** Fire the autosave this long (ms) before the access token expires. */
const LEAD_TIME_MS = 60_000

/**
 * Dev-only testing override. Set `localStorage.autosaveTestDelayMs = '5000'`
 * in the browser console and reload to fire the autosave 5s after mount,
 * bypassing the token-expiry logic. Ignored entirely in production builds.
 */
const getTestDelayMs = (): number | null => {
  if (!import.meta.env.DEV) return null

  const raw = localStorage.getItem('autosaveTestDelayMs')
  const ms = raw ? Number(raw) : NaN

  return Number.isFinite(ms) && ms >= 0 ? ms : null
}

/**
 * Reads the access token expiry (the JWT `exp` claim) from localStorage.
 * Returns the expiry as epoch milliseconds, or null if there is no decodable token.
 */
const getTokenExpiryMs = (): number | null => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY)

  if (!token) return null

  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]))

    return typeof exp === 'number' ? exp * 1000 : null
  } catch {
    return null
  }
}

type Options = {
  /** When false the autosave is not scheduled. */
  enabled?: boolean
  /** Called once, ~1 minute before the session (access token) expires. */
  onAutosave: () => void
}

/**
 * Schedules a single autosave callback ~1 minute before the current session
 * (access token) expires. The token is refreshed transparently on every request,
 * so if a refresh extends the expiry while we wait, the timer reschedules itself
 * instead of firing early.
 */
export const useExpiryAutosave = ({ enabled = true, onAutosave }: Options) => {
  const callbackRef = useRef(onAutosave)
  callbackRef.current = onAutosave

  useEffect(() => {
    if (!enabled) return

    let timer: ReturnType<typeof setTimeout>

    // Dev-only fast path: fire after a fixed short delay for manual testing.
    const testDelayMs = getTestDelayMs()

    if (testDelayMs !== null) {
      timer = setTimeout(() => callbackRef.current(), testDelayMs)

      return () => clearTimeout(timer)
    }

    const schedule = () => {
      const expiryMs = getTokenExpiryMs()

      if (!expiryMs) return

      const delay = Math.max(expiryMs - Date.now() - LEAD_TIME_MS, 0)

      timer = setTimeout(() => {
        const currentExpiryMs = getTokenExpiryMs()

        // The token was refreshed while we waited — reschedule for the new expiry.
        if (currentExpiryMs && currentExpiryMs - Date.now() > LEAD_TIME_MS) {
          schedule()

          return
        }

        callbackRef.current()
      }, delay)
    }

    schedule()

    return () => clearTimeout(timer)
  }, [enabled])
}
